import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { API_BASE } from "../../utils/api/api";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailSignupPage = () => {
  const navigate = useNavigate();
  const { signIn } = useAuthUser();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const title = mode === "login" ? "Sign in with email" : "Create your account";
  const subtitle =
    mode === "login"
      ? "Enter your email and password to get back into Zeqi"
      : "Enter your details to get started";
  const submitLabel = mode === "login" ? "Sign in" : "Continue";
  const switchPrompt =
    mode === "login" ? "Need a new account?" : "Already have an account?";
  const switchLabel = mode === "login" ? "Create one" : "Sign in";

  const canSubmit = useMemo(() => {
    const email = form.email.trim();
    const password = form.password;
    if (!EMAIL_REGEX.test(email) || password.length < 8) return false;
    if (mode === "signup" && !form.name.trim()) return false;
    return true;
  }, [form.email, form.name, form.password, mode]);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLogin = async () => {
    const response = await fetch(`${API_BASE}/api/users/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email.trim(),
        password: form.password,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to sign in");
    }

    signIn({
      userId: data.id,
      id: data.id,
      email: data.email,
      token: data.token,
      isAdmin: data.isAdmin,
    });

    navigate(data.nextRoute || "/", { replace: true });
  };

  const handleSignup = async () => {
    const response = await fetch(`${API_BASE}/api/users/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to register");
    }

    navigate("/login/email/verify", {
      state: {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        await handleLogin();
      } else {
        await handleSignup();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((current) => (current === "login" ? "signup" : "login"));
    setError("");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1.25rem",
        background: "var(--bg-app)",
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          background: "var(--bg-sb)",
          border: "1px solid var(--bd)",
          borderRadius: 24,
          padding: "2.5rem 2rem",
          boxShadow: "var(--app-shadow)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            padding: 6,
            borderRadius: 999,
            background: "var(--bg-app)",
            marginBottom: "1.5rem",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setError("");
            }}
            style={mode === "login" ? activeTabStyle : inactiveTabStyle}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("signup");
              setError("");
            }}
            style={mode === "signup" ? activeTabStyle : inactiveTabStyle}
          >
            Sign up
          </button>
        </div>

        <h2
          style={{
            margin: "0 0 0.25rem",
            fontSize: 22,
            fontWeight: 700,
            color: "var(--tp)",
          }}
        >
          {title}
        </h2>
        <p style={{ margin: "0 0 1.75rem", fontSize: 14, color: "var(--ts2)" }}>
          {subtitle}
        </p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
          {mode === "signup" ? (
            <input
              placeholder="Name"
              value={form.name}
              onChange={update("name")}
              required
              style={inputStyle}
            />
          ) : null}
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={update("email")}
            required
            autoComplete="email"
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={update("password")}
            required
            minLength={8}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            style={inputStyle}
          />
          {error ? (
            <p style={{ fontSize: 13, color: "var(--danger-text)", margin: 0 }}>
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={loading || !canSubmit} style={primaryBtnStyle}>
            {loading ? (mode === "login" ? "Signing in…" : "Sending code…") : submitLabel}
          </button>
        </form>
        <p style={{ marginTop: "1.25rem", fontSize: 13, color: "var(--ts2)", textAlign: "center" }}>
          {switchPrompt}{" "}
          <button
            type="button"
            onClick={switchMode}
            style={{ background: "none", border: "none", color: "var(--ac)", cursor: "pointer", fontWeight: 600, padding: 0 }}
          >
            {switchLabel}
          </button>
        </p>
        <p style={{ marginTop: "0.75rem", fontSize: 13, color: "var(--ts2)", textAlign: "center" }}>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{ background: "none", border: "none", color: "var(--ac)", cursor: "pointer", fontWeight: 600, padding: 0 }}
          >
            Back to connection options
          </button>
        </p>
      </div>
    </div>
  );
};

const inputStyle = {
  width: "100%",
  padding: "0.75rem 0.9rem",
  borderRadius: 12,
  border: "1px solid var(--input-border)",
  background: "var(--input-bg)",
  color: "var(--tp)",
  fontSize: 14,
  boxSizing: "border-box",
};

const primaryBtnStyle = {
  width: "100%",
  padding: "0.875rem",
  borderRadius: 999,
  border: "none",
  background: "var(--ac)",
  color: "var(--hero-contrast)",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  marginTop: "0.25rem",
};

const inactiveTabStyle = {
  border: "none",
  borderRadius: 999,
  background: "transparent",
  color: "var(--ts2)",
  fontSize: 14,
  fontWeight: 700,
  padding: "0.75rem 1rem",
  cursor: "pointer",
};

const activeTabStyle = {
  ...inactiveTabStyle,
  background: "var(--bg-sb)",
  color: "var(--tp)",
  boxShadow: "var(--app-shadow)",
};

export default EmailSignupPage;
